/* ============================================================
   FHAUS Warm — premium motion engine
   Scroll reveals · stagger · parallax · magnetic CTAs · 3D tilt
   · count-up · ripple · scroll progress · directional header.
   Everything is transform/opacity only (GPU-composited) and
   fully disabled under prefers-reduced-motion.
   ============================================================ */
(function () {
  'use strict';

  var mq = window.matchMedia('(prefers-reduced-motion: reduce)');
  var reduce = mq.matches;
  var finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  var root = document.documentElement;

  // raf throttle helper shared by all scroll listeners
  function rafThrottle(fn) {
    var ticking = false;
    return function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () { ticking = false; fn(); });
    };
  }

  /* ---------------------------------------------------------
     1. Scroll reveals + stagger (motion-safe)
     --------------------------------------------------------- */
  if (reduce) {
    root.classList.add('fh-motion-off');
  } else {
    root.classList.add('fh-js-reveal');

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) {
          e.target.classList.add('is-in');
          io.unobserve(e.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });

    var initReveals = function (scope) {
      scope = scope || document;
      scope.querySelectorAll('[data-fh-reveal]').forEach(function (el) { io.observe(el); });
      scope.querySelectorAll('[data-fh-stagger]').forEach(function (group) {
        var step = parseInt(group.getAttribute('data-fh-stagger'), 10) || 70;
        Array.prototype.forEach.call(group.children, function (child, i) {
          if (!child.hasAttribute('data-fh-reveal')) child.setAttribute('data-fh-reveal', 'card');
          child.style.setProperty('--fh-delay', (Math.min(i, 9) * step) + 'ms');
          child.style.transitionDelay = (Math.min(i, 9) * step) + 'ms';
          io.observe(child);
        });
      });
    };

    /* -------------------------------------------------------
       2. Parallax (depth on hero / story media)
       ------------------------------------------------------- */
    var initParallax = function (scope) {
      var els = Array.prototype.slice.call((scope || document).querySelectorAll('[data-fh-parallax]'));
      if (!els.length || window.__fhParallax) return;
      window.__fhParallax = true;
      var update = rafThrottle(function () {
        var vh = window.innerHeight;
        document.querySelectorAll('[data-fh-parallax]').forEach(function (el) {
          var r = el.getBoundingClientRect();
          if (r.bottom < -120 || r.top > vh + 120) return;
          var speed = parseFloat(el.getAttribute('data-fh-parallax')) || 0.12;
          var center = r.top + r.height / 2 - vh / 2;
          el.style.transform = 'translate3d(0,' + (-center * speed).toFixed(1) + 'px,0)';
        });
      });
      window.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update, { passive: true });
      update();
    };

    /* -------------------------------------------------------
       3. Magnetic CTAs — pointer subtly pulls the button
       ------------------------------------------------------- */
    var initMagnetic = function (scope) {
      if (!finePointer) return;
      (scope || document).querySelectorAll('.fhaus .btn, [data-fh-magnetic]').forEach(function (el) {
        if (el.__fhMag) return;
        el.__fhMag = true;
        var strength = parseFloat(el.getAttribute('data-fh-magnetic')) || 0.3;
        el.addEventListener('pointermove', function (ev) {
          var r = el.getBoundingClientRect();
          var mx = ev.clientX - r.left - r.width / 2;
          var my = ev.clientY - r.top - r.height / 2;
          el.style.transform = 'translate(' + (mx * strength).toFixed(1) + 'px,' + (my * strength).toFixed(1) + 'px)';
        });
        el.addEventListener('pointerleave', function () { el.style.transform = ''; });
      });
    };

    /* -------------------------------------------------------
       4. 3D tilt — cards lean toward the cursor
       ------------------------------------------------------- */
    var initTilt = function (scope) {
      if (!finePointer) return;
      (scope || document).querySelectorAll('.fhaus .pcard, .fhaus .need, .fhaus .quote, .fhaus .bundle').forEach(function (el) {
        if (el.__fhTilt) return;
        el.__fhTilt = true;
        el.classList.add('fh-tilt');
        var raf = null;
        el.addEventListener('pointermove', function (ev) {
          if (raf) return;
          raf = requestAnimationFrame(function () {
            raf = null;
            var r = el.getBoundingClientRect();
            var px = (ev.clientX - r.left) / r.width - 0.5;
            var py = (ev.clientY - r.top) / r.height - 0.5;
            el.style.setProperty('--rx', (py * -5).toFixed(2) + 'deg');
            el.style.setProperty('--ry', (px * 5).toFixed(2) + 'deg');
            el.style.setProperty('--gx', (px * 100 + 50).toFixed(1) + '%');
            el.style.setProperty('--gy', (py * 100 + 50).toFixed(1) + '%');
          });
        });
        el.addEventListener('pointerleave', function () {
          el.style.setProperty('--rx', '0deg');
          el.style.setProperty('--ry', '0deg');
        });
      });
    };

    /* -------------------------------------------------------
       5. Count-up — data-fh-count="4.9" data-fh-decimals="1"
          optional data-fh-prefix / data-fh-suffix
       ------------------------------------------------------- */
    var countIO = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        var el = e.target;
        countIO.unobserve(el);
        var target = parseFloat(el.getAttribute('data-fh-count')) || 0;
        var dec = parseInt(el.getAttribute('data-fh-decimals'), 10) || 0;
        var prefix = el.getAttribute('data-fh-prefix') || '';
        var suffix = el.getAttribute('data-fh-suffix') || '';
        var dur = 1500, start = null;
        var tick = function (ts) {
          if (!start) start = ts;
          var p = Math.min((ts - start) / dur, 1);
          var eased = 1 - Math.pow(1 - p, 3);
          el.textContent = prefix + (target * eased).toFixed(dec) + suffix;
          if (p < 1) requestAnimationFrame(tick);
          else el.textContent = prefix + target.toFixed(dec) + suffix;
        };
        requestAnimationFrame(tick);
      });
    }, { threshold: 0.6 });
    var initCount = function (scope) {
      (scope || document).querySelectorAll('[data-fh-count]').forEach(function (el) { countIO.observe(el); });
    };

    /* -------------------------------------------------------
       6. Ripple on buttons / CTAs
       ------------------------------------------------------- */
    document.addEventListener('pointerdown', function (ev) {
      var btn = ev.target.closest('.fhaus .btn, .fhaus .pcard-cta, .fhaus .cart-pill');
      if (!btn) return;
      var r = btn.getBoundingClientRect();
      var span = document.createElement('span');
      span.className = 'fh-ripple';
      var size = Math.max(r.width, r.height);
      span.style.width = span.style.height = size + 'px';
      span.style.left = (ev.clientX - r.left - size / 2) + 'px';
      span.style.top = (ev.clientY - r.top - size / 2) + 'px';
      btn.appendChild(span);
      setTimeout(function () { span.remove(); }, 650);
    }, { passive: true });

    /* -------------------------------------------------------
       7. Reading progress bar
       ------------------------------------------------------- */
    var bar = document.createElement('div');
    bar.className = 'fh-progress';
    bar.setAttribute('aria-hidden', 'true');
    var addBar = function () { if (document.body && !document.querySelector('.fh-progress')) document.body.appendChild(bar); };
    var updateBar = rafThrottle(function () {
      var h = document.documentElement;
      var max = h.scrollHeight - h.clientHeight;
      bar.style.transform = 'scaleX(' + (max > 0 ? h.scrollTop / max : 0).toFixed(4) + ')';
    });
    window.addEventListener('scroll', updateBar, { passive: true });

    var boot = function () {
      initReveals();
      initParallax();
      initMagnetic();
      initTilt();
      initCount();
      addBar();
      updateBar();
    };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
    else boot();

    // Re-init when the customizer re-renders a section
    document.addEventListener('shopify:section:load', function (e) {
      initReveals(e.target);
      initMagnetic(e.target);
      initTilt(e.target);
      initCount(e.target);
    });
  }

  /* ============================================================
     The helpers below run regardless of motion preference.
     ============================================================ */

  /* Directional header — elevate on scroll, hide on scroll-down */
  (function () {
    var lastY = window.scrollY, hideThreshold = 600;
    var apply = rafThrottle(function () {
      var header = document.querySelector('.fh-header');
      if (!header) return;
      var y = window.scrollY;
      header.classList.toggle('is-scrolled', y > 8);
      if (!reduce) {
        if (y > hideThreshold && y > lastY + 6) header.classList.add('is-hidden');
        else if (y < lastY - 6 || y < hideThreshold) header.classList.remove('is-hidden');
      }
      lastY = y;
    });
    window.addEventListener('scroll', apply, { passive: true });
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', apply);
    else apply();
  })();

  /* Mobile menu toggle (delegated — survives customizer re-renders) */
  (function () {
    document.addEventListener('click', function (e) {
      var toggle = e.target.closest('[data-fh-menu-toggle]');
      var panel = document.querySelector('[data-fh-mobile-nav]');
      if (!panel) return;
      if (toggle) {
        var open = panel.classList.toggle('open');
        panel.hidden = false;
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        toggle.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
        toggle.classList.toggle('is-open', open);
        document.body.classList.toggle('fh-no-scroll', open);
        return;
      }
      if (panel.classList.contains('open') && e.target.closest('[data-fh-mobile-nav] a')) {
        panel.classList.remove('open');
        document.body.classList.remove('fh-no-scroll');
        var btn = document.querySelector('[data-fh-menu-toggle]');
        if (btn) { btn.setAttribute('aria-expanded', 'false'); btn.classList.remove('is-open'); }
      }
    });
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      var panel = document.querySelector('[data-fh-mobile-nav]');
      var btn = document.querySelector('[data-fh-menu-toggle]');
      if (panel && panel.classList.contains('open')) {
        panel.classList.remove('open');
        document.body.classList.remove('fh-no-scroll');
        if (btn) { btn.setAttribute('aria-expanded', 'false'); btn.classList.remove('is-open'); btn.focus(); }
      }
    });
  })();

  /* Toast helper (used by product form) */
  window.fhToast = function (msg) {
    var t = document.querySelector('.fh-toast');
    if (!t) {
      t = document.createElement('div');
      t.className = 'fh-toast';
      t.setAttribute('role', 'status');
      t.setAttribute('aria-live', 'polite');
      document.body.appendChild(t);
    }
    t.textContent = msg;
    t.classList.add('show');
    clearTimeout(window.__fhToastId);
    window.__fhToastId = setTimeout(function () { t.classList.remove('show'); }, 2400);
  };

  /* Update header cart count after AJAX add */
  window.fhUpdateCartCount = function () {
    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        document.querySelectorAll('[data-fh-cart-count]').forEach(function (el) {
          el.textContent = cart.item_count;
          var pill = el.closest('.cart-pill');
          if (pill) {
            pill.classList.add('bump');
            setTimeout(function () { pill.classList.remove('bump'); }, 450);
          }
        });
      })
      .catch(function () {});
  };
})();
